import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { CircleCheck, Coins, Phone } from "lucide-react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  Page,
  PageHeader,
  Pagination,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableSkeleton,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";
import { ListFilters, useListFilters } from "@/components/ListFilters";
import type { TableFilterDto } from "@gatiq/shared";
import { useAuth } from "@/auth/AuthProvider";
import { useCan } from "@/auth/access";

/**
 * Paying a karigar out — WIREFRAMES.md "Counter redemptions".
 *
 * The counter pays out of their own balance and is reimbursed later, so this
 * screen is built around one rule: **see before you pay**. Entering the code
 * only looks the request up; nothing moves until the shopkeeper has read the
 * name and the amount in the confirmation and pressed the second button.
 *
 * Laid out like the list pages: totals and the code entry in one strip, the
 * payout history filling the rest of the page.
 */
interface Preview {
  redemptionId: string;
  displayId: string;
  karigarName: string;
  karigarPhone: string | null;
  points: number;
}

interface Paid {
  id: string;
  displayId: string | null;
  points: number;
  karigarName: string;
  karigarPhone: string | null;
  settledAt: string;
  reimbursedAt: string | null;
}

interface PaidPage {
  filters?: TableFilterDto[];
  items: Paid[];
  total: number;
  page: number;
  totalPages: number;
  paidOut: number;
  reimbursed: number;
  outstanding: number;
}

const CODE_LENGTH = 6;
const qcoin = (n: number) => `${n.toLocaleString("en-IN")} QCoin`;

export function CounterRedemptionsPage() {
  const toast = useToast();
  const { user } = useAuth();
  // Seeing past payouts and paying a karigar are separate grants (D-16).
  const canPay = Boolean(useCan()("payouts", "create"));
  // A counter is repaid by the brand later; anyone else pays from the brand's own money.
  const isCounter = user?.role === "counter";

  const [code, setCode] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ points: number; karigarName: string } | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  const [data, setData] = useState<PaidPage | null>(null);
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p: number): Promise<void> => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(p), pageSize: "20" });
      filters.apply(query);
      setData(await api.get<PaidPage>(`/org/me/counter/redemptions?${query}`));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load payouts.");
    } finally {
      setLoading(false);
    }
  }, [toast, filters.apply]);

  useEffect(() => {
    void load(page);
  }, [load, page]);

  function reset(): void {
    setCode("");
    setPreview(null);
    setError(null);
    setDone(null);
    codeRef.current?.focus();
  }

  async function lookup(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) return;
    setError(null);
    setBusy(true);
    try {
      setPreview(await api.post<Preview>("/org/me/counter/lookup", { otp: code }));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not check that code. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function settle(): Promise<void> {
    if (!preview) return;
    setError(null);
    setBusy(true);
    try {
      const res = await api.post<{ points: number; karigarName: string }>("/org/me/counter/settle", {
        redemptionId: preview.redemptionId,
        otp: code,
      });
      setDone(res);
      setPreview(null);
      setCode("");
      setPage(1);
      await load(1);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not complete the payout.");
    } finally {
      setBusy(false);
    }
  }

  const totals: { label: string; value: string }[] = [
    { label: isCounter ? "Paid out by you" : "Paid out", value: data ? qcoin(data.paidOut) : "—" },
    ...(isCounter
      ? [
          { label: "Repaid to you", value: data ? qcoin(data.reimbursed) : "—" },
          { label: "Still owed to you", value: data ? qcoin(data.outstanding) : "—" },
        ]
      : []),
  ];

  return (
    <Page bleed>
      <PageHeader title="Payouts" />

      <div className="flex shrink-0 flex-wrap items-end justify-between gap-x-10 gap-y-4 bg-raised px-5 py-4 lg:px-8">
        <dl className="flex flex-wrap gap-x-10 gap-y-3">
          {totals.map((t) => (
            <div key={t.label}>
              <dt className="text-[11px] font-semibold tracking-[0.08em] text-muted uppercase">{t.label}</dt>
              <dd className="mt-1 font-title text-2xl leading-none font-semibold tabular-nums text-ink">{t.value}</dd>
            </div>
          ))}
        </dl>

        {canPay ? (
          <form className="flex flex-col gap-1.5" noValidate onSubmit={(e) => void lookup(e)}>
            <label className="text-[13px] font-medium text-ink" htmlFor="redeem-code">
              Karigar&rsquo;s payout code
            </label>
            <div className="flex items-center gap-2">
              <input
                aria-describedby={error && !preview ? "redeem-error" : undefined}
                aria-invalid={Boolean(error && !preview)}
                autoComplete="off"
                className={`h-10 w-40 rounded-md border bg-raised px-3 text-center font-mono text-lg tracking-[0.3em] text-ink shadow-control transition-[border-color,box-shadow] placeholder:text-muted/50 focus:border-brand focus:shadow-[0_0_0_3px_var(--gq-color-1-ring)] focus:outline-none ${
                  error && !preview ? "border-danger" : "border-rule hover:border-ink/25"
                }`}
                id="redeem-code"
                inputMode="numeric"
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH));
                  setError(null);
                }}
                placeholder="000000"
                ref={codeRef}
                value={code}
              />
              <Button
                disabled={code.length !== CODE_LENGTH}
                icon={<Coins aria-hidden="true" className="size-4" />}
                loading={busy && !preview}
                type="submit"
              >
                Check code
              </Button>
            </div>
            {error && !preview ? (
              <p className="text-[13px] text-danger" id="redeem-error" role="alert">
                {error}
              </p>
            ) : null}
          </form>
        ) : null}
      </div>

      <Card fill>
        <div className="flex flex-wrap items-center gap-3 border-b border-rule px-5 py-3"><ListFilters definitions={data?.filters} filters={filters} /></div>
        {loading && !data ? (
          <TableSkeleton cols={isCounter ? 5 : 4} />
        ) : data && data.items.length > 0 ? (
          <>
            <Table fill filterable={false}>
              <THead>
                <TR>
                  <TH>Karigar</TH>
                  <TH>Reference</TH>
                  <TH>Paid on</TH>
                  <TH align="right">Amount</TH>
                  {isCounter ? <TH>Repaid</TH> : null}
                </TR>
              </THead>
              <tbody>
                {data.items.map((r) => (
                  <TR key={r.id}>
                    <TD>
                      <span className="font-medium text-ink">{r.karigarName}</span>
                      {r.karigarPhone ? <span className="block text-xs text-muted">{r.karigarPhone}</span> : null}
                    </TD>
                    <TD className="font-mono text-xs text-muted">{r.displayId}</TD>
                    <TD className="text-xs text-muted">
                      {new Date(r.settledAt).toLocaleString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </TD>
                    <TD align="right" numeric>
                      <span className="font-medium text-ink">{qcoin(r.points)}</span>
                    </TD>
                    {isCounter ? (
                      <TD>
                        {r.reimbursedAt ? <Badge tone="success">Repaid</Badge> : <Badge tone="warning">Waiting</Badge>}
                      </TD>
                    ) : null}
                  </TR>
                ))}
              </tbody>
            </Table>
            <div className="shrink-0">
              <Pagination onChange={setPage} page={data.page} total={data.total} totalPages={data.totalPages} />
            </div>
          </>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState message="Payouts you make will be listed here." title="No payouts yet" />
          </div>
        )}
      </Card>

      {/* See before you pay: the name and the amount, then the second button. */}
      <Modal
        footer={
          done ? (
            <Button onClick={reset}>Next karigar</Button>
          ) : (
            <>
              <Button onClick={reset} variant="secondary">
                Cancel
              </Button>
              <Button loading={busy} onClick={() => void settle()}>
                Pay {preview ? qcoin(preview.points) : ""}
              </Button>
            </>
          )
        }
        onClose={reset}
        open={Boolean(preview || done)}
        title={done ? "Payout recorded" : "Confirm payout"}
        width="sm"
      >
        {done ? (
          <div className="flex flex-col items-center gap-2 py-2 text-center" role="status">
            <CircleCheck aria-hidden="true" className="size-10 text-success" />
            <p className="font-title text-xl font-semibold text-ink">
              Pay {qcoin(done.points)} to {done.karigarName}
            </p>
            <p className="text-sm text-muted">Hand over the payout now.</p>
          </div>
        ) : preview ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-rule bg-surface p-4">
              <p className="font-title text-lg font-semibold text-ink">{preview.karigarName}</p>
              {preview.karigarPhone ? (
                <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted">
                  <Phone aria-hidden="true" className="size-3.5" />
                  {preview.karigarPhone}
                </p>
              ) : null}
              <p className="mt-3 font-title text-3xl font-semibold tabular-nums text-ink">{qcoin(preview.points)}</p>
              <p className="mt-1 font-mono text-xs text-muted">{preview.displayId}</p>
            </div>
            {error ? <Alert kind="danger">{error}</Alert> : null}
          </div>
        ) : null}
      </Modal>
    </Page>
  );
}
